import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import {
  EVENT_LOCATION_TYPE_ONLINE,
  LANG_EN,
  LANG_JP,
  UNIT_DAY,
} from "@/constants";
import type { EventFormData } from "@/features/study-group-events/type";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { convertTimezoneToLocal } from "@/lib/utils/dateUtils";

dayjs.extend(timezone);

type BuildInitialFormOptions = {
  // true の場合、日時はイベントの time_zone 基準でそのまま表示用に展開する
  preserveEventTimezoneDateTime?: boolean;
};

/**
 * Build EventFormData initial state from API Event response
 * - Normalizes timezone to local date/time strings
 * - Produces union-conform shape (allDay vs timed)
 * - Applies safe fallbacks for optional API fields
 */
export function buildInitialFormDataFromApiEvent(
  event: EventApiResponse,
  options?: BuildInitialFormOptions,
): EventFormData {
  const useEventTimezone = options?.preserveEventTimezoneDateTime === true;
  const startDisplay = useEventTimezone
    ? formatDateTimeInEventTimeZone(event.start_datetime, event.time_zone)
    : convertTimezoneToLocal(event.start_datetime, event.time_zone);
  const endDisplay = event.end_datetime
    ? useEventTimezone
      ? formatDateTimeInEventTimeZone(event.end_datetime, event.time_zone)
      : convertTimezoneToLocal(event.end_datetime, event.time_zone)
    : startDisplay;

  const common = buildEventFormCommonFields(event);

  // 終日イベント
  if (event.all_day === 1) {
    // Humhub では end_datetime が翌日 00:00 で返ってくるので、1日引いた date を用いる
    const endInclusive = event.end_datetime
      ? dayjs(endDisplay.date).subtract(1, "day").format("YYYY-MM-DD")
      : startDisplay.date;

    return {
      ...common,
      isAllDay: true,
      startDate: startDisplay.date,
      endDate: endInclusive,
    };
  }

  // 時刻指定イベント
  return {
    ...common,
    isAllDay: false,
    startDate: startDisplay.date,
    endDate: endDisplay.date,
    startTime: startDisplay.time,
    endTime: endDisplay.time,
  };
}

const formatDateTimeInEventTimeZone = (
  dateTimeStr: string,
  timeZone: string,
): { date: string; time: string } => {
  try {
    const eventTime = dayjs.tz(dateTimeStr, timeZone);
    return {
      date: eventTime.format("YYYY-MM-DD"),
      time: eventTime.format("HH:mm"),
    };
  } catch (_error) {
    return {
      date: dateTimeStr.split(" ")[0] || "",
      time: dateTimeStr.split(" ")[1]?.slice(0, 5) || "",
    };
  }
};

function buildEventFormCommonFields(event: EventApiResponse) {
  const reminders = Array.isArray(event.reminders)
    ? dedupeReminderItems(
        event.reminders.map((r) => ({ unit: r.unit, value: r.value })),
      )
    : [{ unit: UNIT_DAY, value: 1 }];

  // Normalize location and type with backward compatibility
  const location_type = event.location_type || EVENT_LOCATION_TYPE_ONLINE;
  const location = event.location || "";
  const online_location = event.online_location || "";

  const language = event.language === LANG_JP ? LANG_JP : LANG_EN;

  return {
    title: event.title || "",
    description: event.description || "",
    channel: "",
    files: [] as File[],
    timeZone: event.time_zone || undefined,
    location,
    isAllDay: event.all_day === 1,
    is_public: event.visibility,
    language,
    is_draft: event.is_draft || 0,
    online_location,
    location_type,
    reminders,
    hidden: event?.content?.metadata?.hidden === true ? 1 : 0,
    recurrence: (event as { recurrence?: unknown })?.recurrence as
      | import("@/lib/humhub-api/types").RecurrenceRule
      | null
      | undefined,
    scope: "single" as import("@/lib/humhub-api/types").RecurrenceScope,
  };
}

export const dedupeReminderItems = <T extends { unit: number; value: number }>(
  reminders: T[],
): T[] => {
  const seen = new Set<string>();
  return reminders.filter((reminder) => {
    const key = `${reminder.unit}-${reminder.value}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
